/**
 * Object Normalization Utilities
 *
 * Converts PascalCase object keys to camelCase using functional approach with TypeScript generics. Optimized for
 * Angular 20+ and modern TypeScript standards.
 */

/**
 * Recursively normalizes object keys from PascalCase to camelCase Handles nested objects, arrays, and null/undefined
 * values gracefully Uses functional approach with proper typing
 *
 * @example
 *   normalizePascalToCamelCase({ FirstName: 'John', LastName: 'Doe' });
 *   // Returns: { firstName: 'John', lastName: 'Doe' }
 *
 * @param obj - Object with PascalCase keys to normalize
 * @returns New object with camelCase keys (creates new object, non-mutating)
 */
export function normalizePascalToCamelCase<T extends Record<string, unknown> = Record<string, unknown>>(
  obj: T | T[] | null | undefined
): T | T[] | null | undefined {
  // Null/undefined passthrough
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays recursively
  if (Array.isArray(obj)) {
    return obj.map((item) => normalizePascalToCamelCase(item)) as T[];
  }

  // Primitives passthrough
  if (typeof obj !== 'object') {
    return obj;
  }

  // Transform object keys
  const normalized = {} as Record<string, unknown>;

  for (const [key, value] of Object.entries(obj)) {
    // Convert first character to lowercase (PascalCase → camelCase)
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1);

    // Recursively normalize nested objects/arrays, otherwise use value as-is
    if (value === null || value === undefined) {
      normalized[camelKey] = value;
    } else if (Array.isArray(value) || typeof value === 'object') {
      normalized[camelKey] = normalizePascalToCamelCase(value as any);
    } else {
      normalized[camelKey] = value;
    }
  }

  return normalized as T;
}

/**
 * Type-safe key mapper for transforming object keys Generic utility for applying custom key transformation rules
 *
 * @example
 *   mapObjectKeys({ firstName: 'John' }, (key) => key.toUpperCase());
 *   // Returns: { FIRSTNAME: 'John' }
 *
 * @param obj - Source object
 * @param keyTransformer - Function to transform each key
 * @returns New object with transformed keys
 */
export function mapObjectKeys<T extends Record<string, unknown> = Record<string, unknown>>(
  obj: T | null | undefined,
  keyTransformer: (key: string) => string
): T | null | undefined {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => mapObjectKeys(item as Record<string, unknown>, keyTransformer)) as unknown as T;
  }

  const transformed = {} as Record<string, unknown>;

  for (const [key, value] of Object.entries(obj)) {
    const newKey = keyTransformer(key);

    if (value === null || value === undefined) {
      transformed[newKey] = value;
    } else if (Array.isArray(value) || typeof value === 'object') {
      transformed[newKey] = mapObjectKeys(value as Record<string, unknown>, keyTransformer);
    } else {
      transformed[newKey] = value;
    }
  }

  return transformed as T;
}
